import { useQuery } from "@apollo/client/react";
import { Box, IconicButton, Text } from "@nulogy/components";
import DeleteConfirmationModal from "components/common/DeleteConfirmationModal";
import { GridBox4 } from "components/common/EditableListStyledComponents";
import PageWithBreadcrumbs from "components/pages/PageWithBreadcrumbs";
import { GetDowntimeReasons } from "hooks/apollo/downtimeReason/downtimeReason.gql";
import { removeDowntimeReason } from "hooks/apollo/downtimeReason/helpers/handleUpdateDowntimeReasons";
import useDeleteDowntimeReason from "hooks/apollo/downtimeReason/useDeleteDowntimeReason";
import useDeleteConfirmationModal from "hooks/state/useDeleteConfirmationModal";
import React, { useState } from "react";
import Uuid from "utils/Uuid";
import DowntimeReason from "./DowntimeReason";
import NewDowntimeReason from "./NewDowntimeReason";

export default function DowntimeReasons() {
  const { data, loading } = useQuery(GetDowntimeReasons);
  const [newDowntimeReasonIds, setNewDowntimeReasonIds] = useState([]);
  const { open: openModal } = useDeleteConfirmationModal();
  const deleteDowntimeReason = useDeleteDowntimeReason();

  const handleAddNewDowntimeReason = () => {
    setNewDowntimeReasonIds([...newDowntimeReasonIds, Uuid.generate()]);
  };

  const handleRemoveNewDowntimeReason = (downtimeReasonId) => {
    setNewDowntimeReasonIds(newDowntimeReasonIds.filter((value) => value !== downtimeReasonId));
  };

  const handleDelete = (downtimeReason) => {
    openModal(downtimeReason);
  };

  const onDelete = (downtimeReasonId) =>
    deleteDowntimeReason({
      update: (proxy) => {
        removeDowntimeReason(downtimeReasonId, proxy);
      },
      variables: { id: downtimeReasonId },
    });

  function renderContent() {
    if (loading) return null;

    const hasDowntimeReasons = newDowntimeReasonIds.length > 0 || data.downtimeReasons.length > 0;

    const addButtonLabel = hasDowntimeReasons ? "Add another row" : "Add a downtime reason";
    const deleteMessage =
      "This downtime reason will not be available for scheduling once deleted. However, blocks that it is already applied to will not be impacted.";

    return (
      <Box width="870px">
        <DeleteConfirmationModal message={deleteMessage} onDelete={onDelete} title="Delete downtime reason" />
        <IconicButton
          data-testid="add-button"
          icon="add"
          marginBottom="x2"
          onClick={handleAddNewDowntimeReason}
          type="button"
        >
          {addButtonLabel}
        </IconicButton>
        {hasDowntimeReasons && (
          <Box data-testid="downtime-reasons-table">
            <GridBox4>
              <Text>Reason</Text>
              <Text>Code</Text>
            </GridBox4>
            <hr style={{ marginBottom: "0px" }} />
            {newDowntimeReasonIds.map((newDowntimeReasonId) => (
              <NewDowntimeReason
                downtimeReasonId={newDowntimeReasonId}
                key={newDowntimeReasonId}
                onRemoveNewDowntimeReason={handleRemoveNewDowntimeReason}
              />
            ))}
            {data.downtimeReasons.map((downtimeReason) => (
              <DowntimeReason downtimeReason={downtimeReason} key={downtimeReason.id} onDelete={handleDelete} />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <PageWithBreadcrumbs
      description="Manage your site’s downtime reasons on this page. Once a reason is created, you will have the option to apply it
        to the schedule."
      title="Downtime reasons"
    >
      {renderContent()}
    </PageWithBreadcrumbs>
  );
}
